import { Component, inject, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthService } from '../../../services/authentication/auth.service';
import { ButtonModule } from 'primeng/button';
import { ApiService } from '../../../services/api.service';
import { ICountry } from '../../../models/ICountry';
import { IState } from '../../../models/IState';
import { SelectModule } from 'primeng/select';
import { CustomValidators } from '../../../shared/validators/custom-validators';
import { ShippingAddressService } from '../../../services/bidder-community/shipping-address/shipping-address.service';
import { IShippingAddressDTO } from '../../../models/IShippingAddressDTO ';
import { ToastMessageService } from '../../../services/toast-message/toast-message.service';
import { BidderCommunityMenuService } from '../../../services/bidder-community/bidder-community-menu/bidder-community-menu.service';
import { environment } from '../../../../environments/environment';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { distinctUntilChanged, Subscription } from 'rxjs';


@Component({
  selector: 'app-shipping-address',
  imports: [RouterModule,
    CommonModule,
    ButtonModule,
    SelectModule,
    ReactiveFormsModule,
    ProgressSpinnerModule],
  templateUrl: './shipping-address.component.html',
  styleUrl: './shipping-address.component.scss'
})
export class ShippingAddressComponent implements OnInit {

  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly messageService = inject(ToastMessageService);
  private readonly shippingAddressService = inject(ShippingAddressService);
  private readonly authService = inject(AuthService)
  apiService = inject(ApiService);

  isAuthenticated = this.oidcSecurityService.authenticated;

  countries: ICountry[] = [];
  states: IState[] = [];

  form: FormGroup;
  labelState!: string;
  labelPostalCode!: string;
  placeHolderState!: string;
  defaultCountry!: string;
  defaultstate!: string;
  authSubscription!: Subscription;


  loading = true;



  constructor(private fb: FormBuilder, private bidderCommunityMenuService: BidderCommunityMenuService) {
    this.form = this.fb.group({
      countryCode: ['', [CustomValidators.required('Country')]],
      zipCode: ['', [CustomValidators.required(this.labelPostalCode), CustomValidators.zipCodeValidator('country')]],
      city: ['', [CustomValidators.required('City')]],
      region: ['', [CustomValidators.required('State')]],
      address1: ['', [CustomValidators.required('Shipping Address')]],
    });

  }

  @Input() id!: number;

  ngOnInit(): void {
    setTimeout(() => {
      this.bidderCommunityMenuService.setPageData(
        {
          id: this.id!,
          pageName: "Shipping Address",
          pageRoute: "shipping-address"
        }
      )

      if (typeof window !== 'undefined') {
        window.onpageshow = function (event) {
          if (event.persisted) {
            window.location.reload();
          }
        };
        if (this.isAuthenticated().isAuthenticated) {
          this.onGetShippingAddressInfo();
        } else {
          if (typeof window !== 'undefined') {
            window.open(environment.mediaUrl, '_self');
          }
          return;
        }

      }

      this.authSubscription = this.authService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(isAuth => {
        if (isAuth) {
          this.onGetShippingAddressInfo();
        } else {
          if (typeof window !== 'undefined') {
            window.open(environment.mediaUrl, '_self');
          }
        }
      });
    },100);
  }


  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
  }


  getFieldError(field: string): string | null {
    const control = this.form.get(field);
    if (control && control.errors && (control.dirty || control.touched)) {
      const firstErrorKey = Object.keys(control.errors)[0];
      return control.errors[firstErrorKey];
    }
    return null;
  }

  onGetShippingAddressInfo() {
    this.shippingAddressService.getShippingAddressInfo().subscribe(({
      next: () => {
        const shippingAddressInfoSignal = this.shippingAddressService.shippingAddressInfoSignal();
        // console.log(shippingAddressInfoSignal);
        if (shippingAddressInfoSignal) {
          if (shippingAddressInfoSignal.countryCode) {
            this.onCountries()
            this.defaultCountry = shippingAddressInfoSignal.countryCode;

            this.labelPostalCode = this.defaultCountry === 'US' ? 'Zip Code' : 'Postal Code';
            const zipCodeCtrl = this.form.get('zipCode');
            if (zipCodeCtrl) {
              zipCodeCtrl.setValidators([
                CustomValidators.required(this.labelPostalCode),
                CustomValidators.zipCodeValidator(this.defaultCountry)
              ]);
              zipCodeCtrl.updateValueAndValidity();
            }
          }
          else {
            this.onCountries();
            this.defaultCountry = '';
            this.labelPostalCode = 'Postal Code';
          }
          if (shippingAddressInfoSignal.region) {
            this.form.get('region')?.setValue(shippingAddressInfoSignal.region);
          }

          this.form.patchValue({
            address1: shippingAddressInfoSignal.address1,
            city: shippingAddressInfoSignal.city,
            countryCode: this.defaultCountry,
            zipCode: shippingAddressInfoSignal.zipCode,
            region: shippingAddressInfoSignal.region
          });

          this.loading = false;

          this.onEventCountry(this.defaultCountry);
        }
      },
      error: (err) => {
        console.error('Error retreve data', err);

      },
    }))
  }

  onEventCountry(country: string) {

    this.onLabelState(country);
    this.onStates(country);
    if (country !== this.defaultCountry) {

      this.form.get('region')?.reset();
    }

    this.form.get('region')?.setValidators([
      CustomValidators.required(this.labelState),

      CustomValidators.atLeastOneLetter(this.labelState)
    ]);
    this.form.get('region')?.updateValueAndValidity();


    this.form.get('countryCode')?.valueChanges.subscribe(newCountry => {
      this.onLabelState(newCountry);
      this.onStates(newCountry);

      this.labelPostalCode = newCountry === 'US' ? 'Zip Code' : 'Postal Code';

      this.form.get('zipCode')?.setValidators([
        CustomValidators.required(this.labelPostalCode),
        CustomValidators.zipCodeValidator(newCountry)
      ]);
      this.form.get('zipCode')?.updateValueAndValidity();
      this.form.get('zipCode')?.reset();

      this.onEventCountry(newCountry);
    });
  }


  onCountries() {
    this.apiService.getCountries().subscribe(({
      next: () => {
        const countries = this.apiService.countrySignal();
        if (countries)
          // console.log('countries', countries);
          this.countries = countries;
      }
    }));
  }

  onStates(countrySelected: string) {
    this.apiService.getStates(countrySelected).subscribe(({
      next: () => {
        const states = this.apiService.stateSignal();
        if (states && states.length > 0) {
          this.states = states;
        } else {
          this.states = [];
        }
      }
    }));
  }

  onLabelState(country: string) {
    switch (country) {
      case 'US':
      case 'AU':
        this.labelState = 'State'
        this.placeHolderState = 'Select State'
        break;
      case 'CA':
        this.labelState = 'Province'
        this.placeHolderState = 'Select Province'
        break;
      default:
        this.labelState = 'Region'
        break;
    }
  }

  onSubmit() {

    if (this.form.invalid) {
      this.form.markAllAsTouched;
      return;
    }

    const formData: IShippingAddressDTO = this.form.getRawValue();

    this.shippingAddressService.putAccountInfo(formData).subscribe(({
      next: () => {
        this.messageService.success(`Your Shipping Address has been updated.`);
      },
      error: (err) => {
        let errorMsg = 'unknown error';

        if (typeof err.error === 'string') {
          try {
            const parsed = JSON.parse(err.error);
            errorMsg = parsed.message || errorMsg;

          } catch {
            errorMsg = err.error;
          }
        }
        else if (err.error && err.error.errorMessage) {
          errorMsg = err.error.errorMessage;
        }

        this.messageService.error(errorMsg || 'unknown error');
      }
    }))
  }
}
